import { type FC, type PropsWithChildren, type ReactNode } from 'react'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'
import { Spinner } from '../../../../components/Spinner/Spinner'
import styles from './SchedulingSettings.module.scss'

interface SettingsSectionProps {
  title: string
  subtitle?: string
  titleAdornment?: ReactNode
  action?: ReactNode
  isSaving?: boolean
}

export const SettingsSection: FC<PropsWithChildren<SettingsSectionProps>> = ({ title, subtitle, titleAdornment, action, isSaving = false, children }) => (
  <div className='d-flex flex-column gap-3' aria-busy={isSaving ? true : undefined}>
    <div className='d-flex align-items-center justify-content-between gap-3'>
      <div className='d-flex flex-column'>
        <div className='d-flex align-items-center zocks-gap-0.5'>
          <h4 className='zocks-text-gray-900 mb-0'>{title}</h4>
          {titleAdornment}
          {isSaving ? <Spinner size='sm' /> : null}
        </div>
        {subtitle ? <div className='paragraph-small zocks-text-gray-600'>{subtitle}</div> : null}
      </div>
      {action ? (
        <fieldset disabled={isSaving} className={classNames(isSaving ? 'pe-none opacity-75' : undefined)}>
          {action}
        </fieldset>
      ) : null}
    </div>
    <fieldset disabled={isSaving} className={classNames(isSaving ? 'pe-none opacity-75' : undefined)}>
      <div className='d-flex flex-column gap-3'>{children}</div>
    </fieldset>
  </div>
)

export const SettingsCard: FC<PropsWithChildren<{ className?: string }>> = ({ className, children }) => (
  <div className={classNames('d-flex flex-column border rounded-3 overflow-hidden', className)}>{children}</div>
)

export const SettingsRow: FC<PropsWithChildren<{ className?: string }>> = ({ className, children }) => (
  <div className={classNames(styles.row, 'py-3 zocks-px-1.25', className)}>{children}</div>
)

export const SettingsGroupHeader: FC<{ children: ReactNode }> = ({ children }) => (
  <div className={classNames(styles.groupHeader, 'lead-small zocks-text-gray-600 text-uppercase py-2 zocks-px-1.25 zocks-bg-color-gray-100')}>{children}</div>
)

interface SettingsToggleProps {
  label: string
  description?: ReactNode
  checked: boolean
  readOnly?: boolean
  ariaLabel: string
  onChange: (checked: boolean) => void
}

export const SettingsToggle: FC<SettingsToggleProps> = ({ label, description, checked, readOnly = false, ariaLabel, onChange }) => (
  <div className='d-flex align-items-center justify-content-between gap-3'>
    <div className='d-flex flex-column'>
      <span className='bold'>{label}</span>
      {description ? <span className='paragraph-small zocks-text-gray-600'>{description}</span> : null}
    </div>
    {readOnly ? (
      <span className='paragraph-small zocks-text-gray-700'>{checked ? 'On' : 'Off'}</span>
    ) : (
      <Form.Switch checked={checked} aria-label={ariaLabel} onChange={(event) => onChange(event.currentTarget.checked)} />
    )}
  </div>
)
